// Service 20 — เอกสารบำนาญต่างประเทศ หนังสือรับรองการมีชีวิต (proof of life)
// และบัตรเดินทางนักธุรกิจเอเปก (APEC Business Travel Card — ABTC)
// อ้างอิงหลัก: กรมการกงสุล กองสัญชาติและนิติกรณ์ (consular.mfa.go.th),
// กรมการปกครอง สำนักบริหารการทะเบียน (bora.dopa.go.th),
// สำนักงานตรวจคนเข้าเมือง (immigration.go.th),
// กรมการค้าต่างประเทศ กระทรวงพาณิชย์ — ผู้ออก ABTC ฝ่ายไทย (dft.go.th),
// APEC Business Travel Card scheme (apec.org),
// สภาทนายความในพระบรมราชูปถัมภ์ (lawyerscouncil.or.th),
// HCCH Apostille Convention (hcch.net) — มีผลกับประเทศไทยวันที่ 28 กุมภาพันธ์ 2570
// Client-safe definitions only — no heavy FAQ payload here.

export type PensionFaqCategory =
  | "proof-of-life-basics"
  | "embassy-forms"
  | "thai-authority-route"
  | "translation-legalization"
  | "bank-transfer-payout"
  | "survivor-widow-pension"
  | "abtc-basics"
  | "abtc-documents";

export interface PensionCategoryDef {
  slug: PensionFaqCategory;
  titleTh: string;
  titleEn: string;
  descTh: string;
  descEn: string;
  matchTh: RegExp;
  matchEn: RegExp;
}

export const PENSION_FAQ_CATEGORIES: PensionCategoryDef[] = [
  {
    slug: "proof-of-life-basics",
    titleTh: "พื้นฐานหนังสือรับรองการมีชีวิตสำหรับผู้รับบำนาญ",
    titleEn: "Proof of life certificates for pensioners: the basics",
    descTh:
      "หนังสือรับรองการมีชีวิตคืออะไร ใครเป็นผู้ขอ กองทุนบำนาญต่างประเทศเรียกเอกสารนี้ด้วยชื่อใดบ้าง รอบเวลาที่ต้องส่งกลับ และเหตุที่การส่งช้าทำให้เงินบำนาญถูกระงับชั่วคราว",
    descEn:
      "What a proof-of-life (life certificate) is, who must sign it, the names foreign pension funds use for it, the reporting cycle, and why late returns cause payments to be suspended.",
    matchTh: /รับรองการมีชีวิต|มีชีวิตอยู่|บำนาญ|ผู้รับบำนาญ|รอบปี|ระงับ|ระยะเวลา/i,
    matchEn: /proof of life|life certificate|pension|pensioner|annual|suspend|deadline/i,
  },
  {
    slug: "embassy-forms",
    titleTh: "การรับรองที่สถานทูตหรือสถานกงสุลของประเทศผู้จ่ายบำนาญ",
    titleEn: "Certification at the paying country's embassy or consulate",
    descTh:
      "การนัดหมายที่สถานทูตในไทย แบบฟอร์มเฉพาะของแต่ละกองทุน การลงนามต่อหน้าเจ้าหน้าที่กงสุล และกรณีที่สถานทูตให้ใช้ผู้รับรองในท้องถิ่นแทน",
    descEn:
      "Booking an appointment at the embassy in Thailand, fund-specific forms, signing before a consular officer, and when the embassy instead accepts a local certifier.",
    matchTh: /สถานทูต|สถานกงสุล|กงสุลใหญ่|นัดหมาย|เจ้าหน้าที่กงสุล|แบบฟอร์มของกองทุน/i,
    matchEn: /embassy|consulate|consular officer|appointment|fund form/i,
  },
  {
    slug: "thai-authority-route",
    titleTh: "เส้นทางรับรองผ่านหน่วยงานไทย: อำเภอ ตม. และทนายความผู้ทำคำรับรอง",
    titleEn: "The Thai route: district office, Immigration and notarial services attorney",
    descTh:
      "การขอหนังสือรับรองสถานที่พักอาศัยจากสำนักงานตรวจคนเข้าเมือง การรับรองข้อเท็จจริงที่สำนักทะเบียนอำเภอ และการลงนามต่อหน้าทนายความผู้ทำคำรับรองลายมือชื่อและเอกสารเมื่อกองทุนยอมรับ",
    descEn:
      "Residence certificates from Immigration, factual certifications at the district registrar, and signing before a Notarial Services Attorney when the fund accepts that route.",
    matchTh: /ตรวจคนเข้าเมือง|ตม\.|อำเภอ|สำนักทะเบียน|ทนายความผู้ทำคำรับรอง|ที่พักอาศัย|ถิ่นที่อยู่/i,
    matchEn: /immigration|district office|registrar|notarial services attorney|residence certificate/i,
  },
  {
    slug: "translation-legalization",
    titleTh: "การแปลและนิติกรณ์เอกสารบำนาญเพื่อส่งกลับต่างประเทศ",
    titleEn: "Translation and legalization of pension paperwork",
    descTh:
      "การแปลเอกสารไทยเป็นภาษาของกองทุน การรับรองคำแปลที่กองสัญชาติและนิติกรณ์ กรมการกงสุล ลำดับรับรองเมื่อปลายทางขอตรานิติกรณ์ และผลของอนุสัญญา Apostille ที่จะมีผลกับไทยวันที่ 28 กุมภาพันธ์ 2570",
    descEn:
      "Translating Thai documents into the fund's language, translation certification by the Department of Consular Affairs, the chain when the recipient requires a legalization stamp, and the effect of the Apostille Convention entering into force for Thailand on 28 February 2027.",
    matchTh: /แปล|คำแปล|นิติกรณ์|รับรองคำแปล|Apostille|อโพสทิล|ตรารับรอง/i,
    matchEn: /translation|legalis|legaliz|apostille|certified translation|stamp/i,
  },
  {
    slug: "bank-transfer-payout",
    titleTh: "การรับเงินบำนาญเข้าบัญชีในไทยและเอกสารที่ธนาคารขอ",
    titleEn: "Receiving pension payments in Thailand and what banks ask for",
    descTh:
      "หนังสือรับรองบัญชีธนาคาร เอกสารยืนยันแหล่งเงินได้จากต่างประเทศ การใช้หลักฐานการโอนเงินประกอบการต่อวีซ่าเกษียณ และประเด็นภาษีเงินได้บุคคลธรรมดาตามหลักถิ่นที่อยู่ของกรมสรรพากร",
    descEn:
      "Bank confirmation letters, evidence of foreign-source income, using transfer evidence for retirement-visa extensions, and personal income tax residence questions handled by the Revenue Department.",
    matchTh: /ธนาคาร|บัญชี|โอนเงิน|เงินได้|ภาษี|สรรพากร|วีซ่าเกษียณ|ต่อวีซ่า/i,
    matchEn: /bank|account|transfer|income|tax|revenue|retirement visa|extension/i,
  },
  {
    slug: "survivor-widow-pension",
    titleTh: "บำนาญคู่สมรสและผู้รับผลประโยชน์เมื่อผู้รับบำนาญเสียชีวิต",
    titleEn: "Survivor and spousal pensions after a pensioner dies",
    descTh:
      "การใช้ใบมรณบัตร ทะเบียนสมรส และหนังสือรับรองสถานะจากสำนักทะเบียน การแจ้งกองทุนภายในกำหนด และการคืนเงินที่รับเกินหลังวันเสียชีวิต",
    descEn:
      "Using the death certificate, marriage registration and civil-status certificates, notifying the fund within its deadline, and repaying amounts credited after the date of death.",
    matchTh: /มรณบัตร|เสียชีวิต|คู่สมรส|ทะเบียนสมรส|ผู้รับผลประโยชน์|ทายาท|คืนเงิน/i,
    matchEn: /death certificate|deceased|spouse|marriage|beneficiary|heir|overpayment/i,
  },
  {
    slug: "abtc-basics",
    titleTh: "บัตรเดินทางนักธุรกิจเอเปก (ABTC) — สิทธิและขอบเขต",
    titleEn: "APEC Business Travel Card (ABTC): scope and benefits",
    descTh:
      "ABTC คืออะไร เขตเศรษฐกิจที่เข้าร่วม การใช้ช่องทางพิเศษ APEC lane อายุบัตร และเหตุที่แต่ละเขตเศรษฐกิจต้องอนุมัติแยกกันจึงปรากฏชื่อบนบัตร",
    descEn:
      "What an ABTC is, participating economies, the APEC lane at airports, card validity, and why each economy grants pre-clearance separately before appearing on the card.",
    matchTh: /เอเปก|APEC|ABTC|ช่องทางพิเศษ|บัตรเดินทางนักธุรกิจ|อายุบัตร/i,
    matchEn: /APEC|ABTC|business travel card|lane|pre-clearance|validity/i,
  },
  {
    slug: "abtc-documents",
    titleTh: "เอกสารและขั้นตอนยื่นขอ ABTC ในประเทศไทย",
    titleEn: "ABTC documents and the application process in Thailand",
    descTh:
      "หน่วยงานผู้รับคำขอฝ่ายไทยคือกรมการค้าต่างประเทศ กระทรวงพาณิชย์ เอกสารนิติบุคคลจากกรมพัฒนาธุรกิจการค้า หลักฐานการค้าหรือการลงทุนระหว่างประเทศ หนังสือรับรองความประพฤติ และการต่ออายุบัตร",
    descEn:
      "The Department of Foreign Trade receives Thai applications; company documents from DBD, evidence of international trade or investment, police clearance, and card renewal.",
    matchTh: /กรมการค้าต่างประเทศ|พาณิชย์|เอกสารบริษัท|หนังสือรับรองบริษัท|ความประพฤติ|ต่ออายุ|ยื่นคำขอ/i,
    matchEn: /Department of Foreign Trade|Commerce|company document|police clearance|renew|apply/i,
  },
];

export function getPensionCategoryDef(slug: string): PensionCategoryDef | undefined {
  return PENSION_FAQ_CATEGORIES.find((c) => c.slug === slug);
}